/**
 * Builds pre-generated footer strings at SSR time. Footer always receives these and never uses react-intl.
 */
import type { Messages } from '../../types';

export interface FooterStaticStrings {
  aiTranslationDisclaimer: string;
  copyrightBefore: string;
  copyrightAfter: string;
  resources: string;
  business: string;
  privacy: string;
  terms: string;
  fromTheBlog: string;
  jobcrawlsEn: string;
  jobcrawlsFi: string;
  jobcrawlsSv: string;
  locationsHub: string;
  majorFinnishCities: string;
  jobsInHelsinki: string;
  jobsInTampere: string;
  jobsInEspoo: string;
  jobsInTurku: string;
  jobsInOulu: string;
  jobsInVantaa: string;
  moreFinnishCities: string;
  jobsInVaasa: string;
  jobsInLahti: string;
  jobsInKuopio: string;
  jobsInJyvaskyla: string;
  jobsInPori: string;
  jobsInLappeenranta: string;
  remoteWorkAndCountries: string;
  remoteGlobalJobs: string;
  remoteEuropeJobs: string;
  remoteMENAJobs: string;
  jobsInEurope: string;
  jobsInFinland: string;
}

const MESSAGE_IDS: Record<keyof FooterStaticStrings, string> = {
  aiTranslationDisclaimer: 'footer.aiTranslationDisclaimer',
  copyrightBefore: 'footer.copyrightBefore',
  copyrightAfter: 'footer.copyrightAfter',
  resources: 'footer.resources',
  business: 'footer.business',
  privacy: 'footer.privacy',
  terms: 'footer.terms',
  fromTheBlog: 'footer.fromTheBlog',
  jobcrawlsEn: 'footer.jobcrawlsEn',
  jobcrawlsFi: 'footer.jobcrawlsFi',
  jobcrawlsSv: 'footer.jobcrawlsSv',
  locationsHub: 'footer.locationsHub',
  majorFinnishCities: 'footer.majorFinnishCities',
  jobsInHelsinki: 'footer.jobsInHelsinki',
  jobsInTampere: 'footer.jobsInTampere',
  jobsInEspoo: 'footer.jobsInEspoo',
  jobsInTurku: 'footer.jobsInTurku',
  jobsInOulu: 'footer.jobsInOulu',
  jobsInVantaa: 'footer.jobsInVantaa',
  moreFinnishCities: 'footer.moreFinnishCities',
  jobsInVaasa: 'footer.jobsInVaasa',
  jobsInLahti: 'footer.jobsInLahti',
  jobsInKuopio: 'footer.jobsInKuopio',
  jobsInJyvaskyla: 'footer.jobsInJyvaskyla',
  jobsInPori: 'footer.jobsInPori',
  jobsInLappeenranta: 'footer.jobsInLappeenranta',
  remoteWorkAndCountries: 'footer.remoteWorkAndCountries',
  remoteGlobalJobs: 'footer.remoteGlobalJobs',
  remoteEuropeJobs: 'footer.remoteEuropeJobs',
  remoteMENAJobs: 'footer.remoteMENAJobs',
  jobsInEurope: 'footer.jobsInEurope',
  jobsInFinland: 'footer.jobsInFinland',
};

const DEFAULTS: FooterStaticStrings = {
  aiTranslationDisclaimer: 'Note: Some content on this website contains machine-translations using AI and may contain errors.',
  copyrightBefore: '© 2025 ',
  copyrightAfter: ' All rights reserved.',
  resources: 'Resources',
  business: 'For Employers',
  privacy: 'Privacy Policy',
  terms: 'Terms of Service',
  fromTheBlog: 'From the blog',
  jobcrawlsEn: 'JobCrawls (English)',
  jobcrawlsFi: 'JobCrawls (Finnish)',
  jobcrawlsSv: 'JobCrawls (Swedish)',
  locationsHub: 'Explore location hubs',
  majorFinnishCities: 'Major Finnish Cities',
  jobsInHelsinki: 'Jobs in Helsinki',
  jobsInTampere: 'Jobs in Tampere',
  jobsInEspoo: 'Jobs in Espoo',
  jobsInTurku: 'Jobs in Turku',
  jobsInOulu: 'Jobs in Oulu',
  jobsInVantaa: 'Jobs in Vantaa',
  moreFinnishCities: 'More Finnish Cities',
  jobsInVaasa: 'Jobs in Vaasa',
  jobsInLahti: 'Jobs in Lahti',
  jobsInKuopio: 'Jobs in Kuopio',
  jobsInJyvaskyla: 'Jobs in Jyväskylä',
  jobsInPori: 'Jobs in Pori',
  jobsInLappeenranta: 'Jobs in Lappeenranta',
  remoteWorkAndCountries: 'Remote Work & Countries',
  remoteGlobalJobs: 'Remote - Global Jobs',
  remoteEuropeJobs: 'Remote - Europe Jobs',
  remoteMENAJobs: 'Remote - MENA Jobs',
  jobsInEurope: 'Jobs in Europe',
  jobsInFinland: 'Jobs in Finland',
};

/** Parse "© 2025 {company} All rights reserved." into before/after. */
function parseCopyright(template: string): { before: string; after: string } {
  const idx = template.indexOf('{company}');
  if (idx === -1) return { before: template, after: '' };
  return {
    before: template.slice(0, idx),
    after: template.slice(idx + '{company}'.length),
  };
}

export function buildFooterStaticStrings(messages: Messages): FooterStaticStrings {
  const rawCopyright = messages['footer.copyright'] ?? '© 2025 {company} All rights reserved.';
  const { before: copyrightBefore, after: copyrightAfter } = parseCopyright(rawCopyright);

  const get = (key: keyof FooterStaticStrings): string =>
    messages[MESSAGE_IDS[key]] ?? DEFAULTS[key];

  return {
    aiTranslationDisclaimer: get('aiTranslationDisclaimer'),
    copyrightBefore,
    copyrightAfter,
    resources: get('resources'),
    business: get('business'),
    privacy: get('privacy'),
    terms: get('terms'),
    fromTheBlog: get('fromTheBlog'),
    jobcrawlsEn: get('jobcrawlsEn'),
    jobcrawlsFi: get('jobcrawlsFi'),
    jobcrawlsSv: get('jobcrawlsSv'),
    locationsHub: get('locationsHub'),
    majorFinnishCities: get('majorFinnishCities'),
    jobsInHelsinki: get('jobsInHelsinki'),
    jobsInTampere: get('jobsInTampere'),
    jobsInEspoo: get('jobsInEspoo'),
    jobsInTurku: get('jobsInTurku'),
    jobsInOulu: get('jobsInOulu'),
    jobsInVantaa: get('jobsInVantaa'),
    moreFinnishCities: get('moreFinnishCities'),
    jobsInVaasa: get('jobsInVaasa'),
    jobsInLahti: get('jobsInLahti'),
    jobsInKuopio: get('jobsInKuopio'),
    jobsInJyvaskyla: get('jobsInJyvaskyla'),
    jobsInPori: get('jobsInPori'),
    jobsInLappeenranta: get('jobsInLappeenranta'),
    remoteWorkAndCountries: get('remoteWorkAndCountries'),
    remoteGlobalJobs: get('remoteGlobalJobs'),
    remoteEuropeJobs: get('remoteEuropeJobs'),
    remoteMENAJobs: get('remoteMENAJobs'),
    jobsInEurope: get('jobsInEurope'),
    jobsInFinland: get('jobsInFinland'),
  };
}
